import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import TransitionOverlay from "@/components/TransitionOverlay";
import { useToast } from "@/hooks/use-toast";
import { signOutUser } from "@/lib/auth";
import { functions } from "@/lib/firebase";
import { httpsCallable } from "firebase/functions";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { APP_ROUTES } from "@/lib/appRoutes";
import { validateMarketingRequest } from "@shared/validation/marketingRequest";

export default function RequestAccess() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingRequest, setHasExistingRequest] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Signing out already shows the login screen at the current URL. Only the public
  // request-access page itself is left for the root; any other URL (an org link a
  // zero-org account opened) is kept so the next account to sign in opens it.
  const returnToLogin = () => {
    if (location.pathname === APP_ROUTES.requestAccess) {
      navigate("/");
    }
  };

  // Email is locked to user's authenticated email, or editable for public access
  const email = user?.email || emailInput;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Use shared validation (same as Cloud Function)
    const validation = validateMarketingRequest({ name, email, organizationName });

    if (!validation.valid) {
      toast({ title: validation.error });
      return;
    }

    try {
      setSubmitting(true);
      const submitMarketingRequest = httpsCallable(functions, "submitMarketingRequest");
      await submitMarketingRequest({
        name: validation.normalized!.name,
        email: validation.normalized!.email,
        organizationName: validation.normalized!.organizationName,
      });
      const { dismiss } = toast({
        title: "Request submitted!",
        description: "We'll be in touch. Redirecting to login in 5 seconds...",
      });
      setName("");
      setOrganizationName("");
      setEmailInput("");
      setTimeout(async () => {
        dismiss();
        if (user) {
          await signOutUser();
        }
        returnToLogin();
      }, 5000);
    } catch (caught: unknown) {
      console.error("Error submitting marketing request", caught);
      const error = caught as { code?: string; message?: string } | undefined;
      // Handle specific error codes from the function
      if (error?.code === "functions/already-exists") {
        setHasExistingRequest(true);
      } else if (error?.code === "functions/invalid-argument") {
        // Show the specific validation error from the server
        toast({ title: error.message || "Invalid input" });
      } else {
        toast({ title: "Something went wrong. Please try again later." });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (hasExistingRequest) {
    return (
      <>
        <TransitionOverlay
          open={submitting}
          title="Submitting request..."
          subtitle="Please wait..."
        />
        <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
          <div className="w-full max-w-md space-y-6 bg-white p-8 rounded-lg shadow">
            <h1 className="text-2xl font-semibold text-center">Request Already Submitted</h1>
          <p className="text-muted-foreground text-center">
            You've already submitted a request for access. We'll be in touch soon at <span className="font-medium">{email}</span>.
          </p>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={async () => {
              try {
                await signOutUser();
              } finally {
                returnToLogin();
              }
            }}>
            Back to Login
          </Button>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      <TransitionOverlay
        open={submitting}
        title="Submitting request..."
        subtitle="Please wait..."
      />
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="w-full max-w-md space-y-6 bg-white p-8 rounded-lg shadow">
        <h1 className="text-2xl font-semibold text-center">Request Access to GleanAg</h1>
        <p className="text-muted-foreground text-center">
          Our records show you don't belong to any organizations yet. Provide your details below and we'll get you set
          up.
        </p>
        <form
          onSubmit={handleSubmit}
          className="space-y-4">
          <div>
            <label
              className="block mb-1 font-medium"
              htmlFor="name">
              Your Name
            </label>
            <Input
              id="name"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label
              className="block mb-1 font-medium"
              htmlFor="email">
              Email
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => !user && setEmailInput(e.target.value)}
              disabled={!!user}
              className={user ? "bg-gray-50 cursor-not-allowed" : ""}
            />
          </div>
          <div>
            <label
              className="block mb-1 font-medium"
              htmlFor="organizationName">
              Company Name <span className="text-muted-foreground">(optional)</span>
            </label>
            <Input
              id="organizationName"
              placeholder="Your company or organization"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
            />
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={submitting}>
            {submitting ? "Submitting..." : "Submit Request"}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={async () => {
              try {
                await signOutUser();
              } finally {
                returnToLogin();
              }
            }}>
            Back to Login
          </Button>
          <div className="text-center pt-4">
            <a href="/privacy-policy" className="text-sm text-muted-foreground hover:text-primary">
              Privacy Policy
            </a>
          </div>
        </form>
      </div>
    </div>
    </>
  );
}
